// Admin — Manual invoice detail / printable view / send.
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Printer, Mail, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { site, companyRegLine } from "@/lib/site";
import {
  adminGetManualInvoice,
  adminUpdateManualInvoiceStatus,
  adminEmailManualInvoice,
} from "@/lib/api/manual-invoices.functions";
import logoAsset from "@/assets/logo.jpg.asset.json";

export const Route = createFileRoute(
  "/_authenticated/portal/admin/manual-invoices/$id",
)({
  head: () => ({ meta: [{ title: "Admin · Manual invoice" }] }),
  component: ManualInvoiceDetail,
});

const rZAR = (c: number) => `R${(c / 100).toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ManualInvoiceDetail() {
  const { id } = useParams({ from: "/_authenticated/portal/admin/manual-invoices/$id" });
  const getFn = useServerFn(adminGetManualInvoice);
  const setStatus = useServerFn(adminUpdateManualInvoiceStatus);
  const emailFn = useServerFn(adminEmailManualInvoice);
  const qc = useQueryClient();
  const [altEmail, setAltEmail] = useState("");

  const { data: inv, isLoading } = useQuery({
    queryKey: ["manual-invoice", id],
    queryFn: () => getFn({ data: { id } }),
  });

  const statusMut = useMutation({
    mutationFn: (status: "sent" | "paid" | "cancelled" | "draft") =>
      setStatus({ data: { id, status } }),
    onSuccess: () => {
      toast.success("Status updated");
      qc.invalidateQueries({ queryKey: ["manual-invoice", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sendMut = useMutation({
    mutationFn: () => emailFn({ data: { id, recipientEmail: altEmail || undefined } }),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Emailed to ${r.sentTo}`);
      else toast.error(r.reason || "Send failed");
      qc.invalidateQueries({ queryKey: ["manual-invoice", id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !inv) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <Link to="/portal/admin/manual-invoices" className="inline-flex items-center gap-1 text-sm text-navy hover:underline">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => window.print()}>
            <Printer className="mr-2 h-4 w-4" /> Print / Save PDF
          </Button>
          <Input placeholder={inv.client_email || "recipient@email"} value={altEmail} onChange={(e) => setAltEmail(e.target.value)} className="w-56" />
          <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending || (!inv.client_email && !altEmail)}>
            {sendMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            Email invoice
          </Button>
          {inv.status !== "paid" && (
            <Button variant="outline" onClick={() => statusMut.mutate("paid")}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Mark paid
            </Button>
          )}
          {inv.status !== "cancelled" && (
            <Button variant="outline" onClick={() => statusMut.mutate("cancelled")}>
              <XCircle className="mr-2 h-4 w-4" /> Cancel
            </Button>
          )}
        </div>
      </div>

      <article className="mx-auto max-w-3xl rounded-lg border border-border bg-white p-8 shadow-sm print:border-0 print:shadow-none">
        <header className="flex items-start justify-between border-b border-border pb-6">
          <div className="flex gap-4">
            <img src={logoAsset.url} alt="" className="h-16 w-16 rounded-md object-cover" />
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold">Tax Invoice</p>
              <h1 className="mt-1 font-display text-xl text-navy">{site.name}</h1>
              <p className="text-[11px] text-muted-foreground">{companyRegLine()}</p>
              <p className="text-[11px] text-muted-foreground">{site.address.line1}, {site.address.line2}, {site.address.postalCode}</p>
              <p className="text-[11px] text-muted-foreground">{site.email} · {site.phone}</p>
            </div>
          </div>
          <div className="text-right text-sm">
            <p className="font-mono text-navy">{inv.reference}</p>
            <p className="text-xs text-muted-foreground">Issued: {inv.issued_date}</p>
            {inv.due_date && <p className="text-xs text-muted-foreground">Due: {inv.due_date}</p>}
            <p className="mt-2"><span className={`inline-block rounded px-2 py-0.5 text-xs ${inv.status === "paid" ? "bg-emerald-100 text-emerald-800" : inv.status === "sent" ? "bg-sky-100 text-sky-800" : inv.status === "cancelled" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>{inv.status.toUpperCase()}</span></p>
          </div>
        </header>

        <section className="mt-6 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Billed to</p>
            <p className="mt-1 font-medium text-navy">{inv.client_name}</p>
            {inv.client_email && <p className="text-xs text-muted-foreground">{inv.client_email}</p>}
            {inv.client_phone && <p className="text-xs text-muted-foreground">{inv.client_phone}</p>}
            {inv.client_address && <p className="text-xs text-muted-foreground">{inv.client_address}</p>}
            {inv.client_registration && <p className="text-xs text-muted-foreground">Reg: {inv.client_registration}</p>}
            {inv.client_vat_number && <p className="text-xs text-muted-foreground">VAT: {inv.client_vat_number}</p>}
          </div>
          {inv.notes && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Notes</p>
              <p className="mt-1 text-sm text-navy/80 whitespace-pre-wrap">{inv.notes}</p>
            </div>
          )}
        </section>

        <table className="mt-6 w-full text-sm">
          <thead className="bg-secondary text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(inv.line_items as any[]).map((li, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-3 py-2">{li.description}</td>
                <td className="px-3 py-2 text-right">{li.quantity}</td>
                <td className="px-3 py-2 text-right">{rZAR(li.unit_price_cents)}</td>
                <td className="px-3 py-2 text-right">{rZAR(Math.round(li.quantity * li.unit_price_cents))}</td>
              </tr>
            ))}
            <tr className="border-t border-border">
              <td colSpan={3} className="px-3 py-2 text-right text-muted-foreground">Subtotal</td>
              <td className="px-3 py-2 text-right">{rZAR(inv.subtotal_cents)}</td>
            </tr>
            {inv.vat_cents > 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-muted-foreground">VAT (15%)</td>
                <td className="px-3 py-2 text-right">{rZAR(inv.vat_cents)}</td>
              </tr>
            )}
            <tr className="bg-secondary/50">
              <td colSpan={3} className="px-3 py-3 text-right font-semibold text-navy">Total due</td>
              <td className="px-3 py-3 text-right font-display text-lg text-navy">{rZAR(inv.total_cents)}</td>
            </tr>
          </tbody>
        </table>

        <section className="mt-6 rounded-md border border-border bg-secondary/30 p-4 text-sm">
          <p className="text-[11px] uppercase tracking-wider text-gold">Banking details</p>
          <dl className="mt-2 grid grid-cols-2 gap-y-1">
            <dt className="text-muted-foreground">Account name</dt><dd>{site.banking.accountName}</dd>
            <dt className="text-muted-foreground">Bank</dt><dd>{site.banking.bank}</dd>
            <dt className="text-muted-foreground">Account no.</dt><dd className="font-mono">{site.banking.accountNumber}</dd>
            <dt className="text-muted-foreground">Branch code</dt><dd className="font-mono">{site.banking.branchCode}</dd>
            <dt className="text-muted-foreground">Reference</dt><dd className="font-semibold text-navy">{inv.reference}</dd>
          </dl>
        </section>

        <footer className="mt-6 border-t border-border pt-4 text-[11px] text-muted-foreground">
          {site.name} ({site.registration.entityType}) · {companyRegLine()} · Registered with the Companies and Intellectual Property Commission (CIPC) of the Republic of South Africa.
          {!site.registration.vatNumber && " Not registered for VAT."}
        </footer>
      </article>
    </div>
  );
}
